import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Spinner } from 'frosted-ui';
import { ArrowUpRightFromSquare16, Checkmark16, ExclamationTriangle16 } from '@frosted-ui/icons';
import BuyerNotice from '../components/BuyerNotice';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { api } from '../services/api';
import type { PurchaseInfo } from '../types';

const OBJECT_ID = /^[a-f0-9]{24}$/i;
const WHOP_HUB = 'https://whop.com/hub';

/**
 * Where Whop sends a buyer after a membership checkout. Newer checkouts
 * redirect to /thank-you?test=<experimentId> and the page names the seller
 * and product; older ones (plain /thank-you) get the generic message. Whop
 * appends its own params (`payment`, `status`, and on older checkouts
 * `receipt_id`); a failed or canceled `status` isn't a purchase.
 */
export default function ThankYouPage() {
  const [params] = useSearchParams();
  const testId = params.get('test');
  const status = params.get('status');
  const failed = status === 'failed' || status === 'canceled';
  const lookup = !failed && testId && OBJECT_ID.test(testId) ? testId : null;

  const [info, setInfo] = useState<PurchaseInfo | null>(null);
  const [loading, setLoading] = useState(Boolean(lookup));

  useEffect(() => {
    if (!lookup) return;
    let cancelled = false;
    api
      .getPurchaseInfo(lookup)
      .then((data) => {
        if (!cancelled) setInfo(data);
      })
      .catch(() => {
        // Unknown test or seller: the generic message is still right.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lookup]);

  useDocumentTitle(failed ? 'Payment not completed' : 'Purchase complete');

  if (loading) {
    return (
      <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="2" />
      </div>
    );
  }

  if (failed) {
    return (
      <BuyerNotice
        icon={<ExclamationTriangle16 style={{ width: 24, height: 24 }} />}
        title="Your payment didn't go through"
        body="No money was taken. Go back to the link you used and try again, or use another payment method."
      />
    );
  }

  const product = info?.productName;
  const title = info ? `You're in — ${product ?? 'your purchase'} from ${info.sellerName}` : "You're in";
  const linkLabel = product ? `Open ${product} on Whop` : 'Open it on Whop';

  return (
    <BuyerNotice
      icon={<Checkmark16 style={{ width: 24, height: 24 }} />}
      title={title}
      body={
        info
          ? 'Whop is setting up your access and will email your receipt.'
          : 'Your purchase went through. Whop is setting up your access and will email your receipt.'
      }
      actions={
        <Button asChild size="3" variant="solid" className="pl-tap" style={{ width: '100%' }}>
          <a href={info?.productUrl ?? WHOP_HUB}>
            {info ? linkLabel : 'Go to your Whop account'}
            <ArrowUpRightFromSquare16 />
          </a>
        </Button>
      }
    />
  );
}
